import { useEffect } from 'react'
import type { SmpteController } from '../BitmovinPlayer/types'

/**
 * Angel Player keyboard shortcuts hook.
 *
 * This hook centralizes all keyboard shortcut behavior for the Angel video player.
 * It is intentionally kept separate from `AngelPlayerContext` so that:
 * - Keyboard concerns are isolated from playback / tracking state management
 * - The same behavior can be reused or evolved independently of the context
 * - The rules for when we *do not* capture keys are clearly documented in one place
 *
 * Behavior:
 * - Listens for `keydown` on `window`, but only reacts when:
 *   - There is an active `SmpteController`
 *   - The Angel player container (`.angel-player`) is present on the page
 *   - The target is **not** an input/textarea/contenteditable
 *   - The device has a "fine" pointer (desktop; avoids hijacking mobile/touch)
 * - After any seek, it calls `onSeek` so callers can track analytics / state transitions.
 *
 * Supported shortcuts (YouTube-style):
 * - Space / K: Toggle play/pause
 * - J / ArrowLeft: Seek backward 10s
 * - L / ArrowRight: Seek forward 10s
 * - ArrowUp / ArrowDown: Volume up/down 5%
 * - M: Toggle mute
 * - F: Toggle fullscreen
 * - 0-9: Seek to 0%-90% of video duration
 * - Home / End: Seek to beginning / end of video
 * - < / > (Shift+comma / Shift+period): Decrease / increase playback speed
 * - , / . (when paused): Frame step backward / forward
 * - C: Toggle captions
 */

const SEEK_SECONDS = 10
const VOLUME_STEP = 5
const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 1.5, 2]

interface UsePlayerKeyboardShortcutsOptions {
  controller?: SmpteController
  onSeek: () => void
  /**
   * Optional duration of the current video in seconds.
   * When provided, numeric keys and right-arrow seeks will be clamped to this value.
   */
  durationSeconds?: number
}

/**
 * Registers global keyboard shortcuts for the Angel player while the given
 * controller is active and the user has interacted with the player.
 *
 * This is intentionally a `window`-level listener so that users can seek
 * even when focus is on non-focusable regions inside the player UI. To
 * avoid surprising UX, we strictly scope handling to events that originate
 * within the Angel player container and skip text-entry elements.
 */
export function usePlayerKeyboardShortcuts({
  controller,
  onSeek,
  durationSeconds,
}: UsePlayerKeyboardShortcutsOptions): void {
  useEffect(() => {
    if (!controller) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isKeyboardEventEligible(event)) handlePlayerKeyDown(event, controller, onSeek, durationSeconds)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [controller, onSeek, durationSeconds])
}

function isKeyboardEventEligible(event: KeyboardEvent): boolean {
  const isDesktop = window.matchMedia('(pointer: fine)').matches
  if (!isDesktop) return false

  const angelPlayerElement = document.querySelector<HTMLElement>('.angel-player')
  if (!angelPlayerElement) return false

  const target = event.target as HTMLElement | null
  if (!target) return false

  if (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable ||
    target.closest('input, textarea, [contenteditable]')
  ) {
    return false
  }

  return true
}

function handlePlayerKeyDown(
  event: KeyboardEvent,
  controller: SmpteController,
  onSeek: () => void,
  durationSeconds?: number,
): void {
  switch (event.key) {
    case 'ArrowLeft':
    case 'j':
    case 'J':
      handleSeekBackward(event, controller, onSeek)
      return

    case 'ArrowRight':
    case 'l':
    case 'L':
      handleSeekForward(event, controller, onSeek, durationSeconds)
      return

    case ' ':
    case 'Spacebar':
    case 'k':
    case 'K':
      handleTogglePlayPause(event, controller)
      return

    case 'ArrowUp':
      handleVolumeUp(event, controller)
      return

    case 'ArrowDown':
      handleVolumeDown(event, controller)
      return

    case 'm':
    case 'M':
      handleToggleMute(event, controller)
      return

    case 'f':
    case 'F':
      handleToggleFullscreen(event, controller)
      return

    case 'Home':
      handleSeekToBeginning(event, controller, onSeek)
      return

    case 'End':
      handleSeekToEnd(event, controller, onSeek, durationSeconds)
      return

    case '<':
      handleDecreaseSpeed(event, controller)
      return

    case '>':
      handleIncreaseSpeed(event, controller)
      return

    case ',':
      handleFrameStepBackward(event, controller)
      return

    case '.':
      handleFrameStepForward(event, controller)
      return

    case 'c':
    case 'C':
      handleToggleCaptions(event, controller)
      return

    default:
      break
  }

  // Handle 0-9 keys for percentage seeking
  if (event.key >= '0' && event.key <= '9' && typeof durationSeconds === 'number') {
    handleSeekToPercentage(event, controller, onSeek, durationSeconds)
  }
}

function handleSeekBackward(event: KeyboardEvent, controller: SmpteController, onSeek: () => void): void {
  event.preventDefault()
  const currentTime = controller.getCurrentTime()
  controller.seek(Math.max(0, currentTime - SEEK_SECONDS))
  onSeek()
}

function handleSeekForward(
  event: KeyboardEvent,
  controller: SmpteController,
  onSeek: () => void,
  durationSeconds?: number,
): void {
  event.preventDefault()
  const currentTime = controller.getCurrentTime()
  const proposedTime = currentTime + SEEK_SECONDS
  const newTime = typeof durationSeconds === 'number' ? Math.min(durationSeconds, proposedTime) : proposedTime
  controller.seek(newTime)
  onSeek()
}

function handleTogglePlayPause(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  if (controller.isPlaying()) controller.pause()
  else controller.play()
}

function handleVolumeUp(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  const currentVolume = controller.getVolume()
  controller.setVolume(Math.min(100, currentVolume + VOLUME_STEP))
}

function handleVolumeDown(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  const currentVolume = controller.getVolume()
  controller.setVolume(Math.max(0, currentVolume - VOLUME_STEP))
}

function handleToggleMute(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  if (controller.isMuted()) controller.unmute()
  else controller.mute()
}

function handleToggleFullscreen(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  if (controller.isFullscreen()) controller.exitFullscreen()
  else controller.enterFullscreen()
}

function handleSeekToBeginning(event: KeyboardEvent, controller: SmpteController, onSeek: () => void): void {
  event.preventDefault()
  controller.seek(0)
  onSeek()
}

function handleSeekToEnd(
  event: KeyboardEvent,
  controller: SmpteController,
  onSeek: () => void,
  durationSeconds?: number,
): void {
  event.preventDefault()
  if (typeof durationSeconds === 'number') {
    controller.seek(durationSeconds)
    onSeek()
  }
}

function handleDecreaseSpeed(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  const currentSpeed = controller.getPlaybackSpeed()
  const slowerSpeed = PLAYBACK_SPEEDS.filter((s) => s < currentSpeed).pop()
  if (slowerSpeed !== undefined) controller.setPlaybackSpeed(slowerSpeed)
}

function handleIncreaseSpeed(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  const currentSpeed = controller.getPlaybackSpeed()
  const fasterSpeed = PLAYBACK_SPEEDS.find((s) => s > currentSpeed)
  if (fasterSpeed !== undefined) controller.setPlaybackSpeed(fasterSpeed)
}

function handleFrameStepBackward(event: KeyboardEvent, controller: SmpteController): void {
  if (controller.isPlaying()) return
  event.preventDefault()
  controller.step(-1)
}

function handleFrameStepForward(event: KeyboardEvent, controller: SmpteController): void {
  if (controller.isPlaying()) return
  event.preventDefault()
  controller.step(1)
}

function handleToggleCaptions(event: KeyboardEvent, controller: SmpteController): void {
  event.preventDefault()
  const subtitles = controller.getSubtitleList()
  if (!subtitles || subtitles.length === 0) return

  const activeSubtitle = subtitles.find((s) => s.enabled)
  if (activeSubtitle) {
    controller.disableSubTitle(activeSubtitle.id)
  } else {
    const firstSubtitle = subtitles[0]
    if (firstSubtitle) controller.enableSubTitle(firstSubtitle.id)
  }
}

function handleSeekToPercentage(
  event: KeyboardEvent,
  controller: SmpteController,
  onSeek: () => void,
  durationSeconds: number,
): void {
  event.preventDefault()
  const percentage = parseInt(event.key, 10) / 10
  controller.seek(durationSeconds * percentage)
  onSeek()
}
